"use client";

import { CallIcon, EmailIcon, MapIcon } from "@/assets/icons";
import Breadcrumb from "@/components/Common/Breadcrumb";
import Loader from "@/components/Common/Loader";
import { InputGroup } from "@/components/ui/input";
import cn from "@/utils/cn";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useEcommerce } from "../EcommerceProvider/EcommerceProvider";
import { formatAddress, getLegalFormLabel } from "@/utils/company";
import { useTranslations } from "next-intl";
import Link from "next/link";

type Input = {
  firstName: string;
  lastName: string;
  subject: string;
  phone: string;
  message: string;
};

const Contact = () => {
  const { register, control, formState, handleSubmit } = useForm<Input>({
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      subject: "",
      phone: "",
      message: "",
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const ecommerce = useEcommerce();
  const t = useTranslations("Contact");

  const addressText =
    formatAddress(ecommerce.companyAddress) ||
    formatAddress(ecommerce.correspondenceAddress);

  const phoneText = [
    ecommerce.contact?.phonePrefix,
    ecommerce.contact?.phoneNumber,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const phoneHref = ecommerce.contact?.phoneNumber
    ? `tel:${(ecommerce.contact.phonePrefix || "") + ecommerce.contact.phoneNumber}`.replace(
        /\s+/g,
        ""
      )
    : null;
  const otherPhones = ecommerce.contact?.otherPhonesNumbers ?? [];
  const phonePrefix = (ecommerce.contact?.phonePrefix ?? "").trim();

  const email = ecommerce.contact?.email || null;

  const onSubmit = (data: Input) => {
    setIsLoading(true);
    // Handle form submission
    console.log(data);
    setIsLoading(false);
  };

  return (
    <>
      <Breadcrumb title={t("title")} pages={[t("title")]} />

      <section className="py-20 overflow-hidden bg-gray-2">
        <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 xl:px-0">
          <div className="flex flex-col xl:flex-row gap-7.5">
            <div className="w-full bg-white xl:w-1/3 rounded-xl shadow-1">
              <div className="py-5 px-4 sm:px-7.5 border-b border-gray-3">
                <p className="text-xl font-medium text-dark">
                  {t("contactInformation")}
                </p>
              </div>

              <div className="p-4 sm:p-7.5">
                <div className="flex flex-col gap-3.5">
                  {email && (
                    <p className="flex items-center gap-3.5">
                      <EmailIcon className="w-6 h-6 text-blue" />
                      {t("emailLabel")}: {email}
                    </p>
                  )}

                  {phoneText && phoneHref && (
                    <div className="flex flex-col gap-1.5">
                      <Link
                        href={phoneHref}
                        className="flex items-center gap-3.5"
                      >
                        <CallIcon className="w-6 h-6 text-blue" />
                        {t("phoneLabel")}: {phoneText}
                      </Link>

                      {otherPhones.map((phone, idx) =>
                        phone.phoneNumber
                          ? (() => {
                              const fullPhone = [phonePrefix, phone.phoneNumber]
                                .filter(Boolean)
                                .join(" ")
                                .trim();

                              return (
                                <Link
                                  key={idx}
                                  href={`tel:${fullPhone.replace(/\s+/g, "")}`}
                                  className="ml-10 "
                                >
                                  {phone.name ? `${phone.name}: ` : ""}
                                  {fullPhone}
                                </Link>
                              );
                            })()
                          : null
                      )}
                    </div>
                  )}

                  {addressText && (
                    <p className="flex gap-3.5">
                      <MapIcon className="w-6 h-6 text-blue shrink-0" />
                      {t("addressLabel")}: {addressText}
                    </p>
                  )}
                </div>

                {/* Company Info */}
                {ecommerce.companyInfo && (
                  <div className="mt-6 text-sm text-gray-600">
                    {ecommerce.companyInfo.legalForm && (
                      <p>
                        {getLegalFormLabel(ecommerce.companyInfo.legalForm)}
                      </p>
                    )}
                    {ecommerce.companyInfo.nip && (
                      <p>NIP: {ecommerce.companyInfo.nip}</p>
                    )}
                    {ecommerce.companyInfo.regon && (
                      <p>REGON: {ecommerce.companyInfo.regon}</p>
                    )}
                    {ecommerce.companyInfo.krs && (
                      <p>KRS: {ecommerce.companyInfo.krs}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="xl:w-2/3 w-full bg-white rounded-xl shadow-1 p-4 sm:p-7.5 xl:p-10">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-5 mb-5 lg:flex-row sm:gap-8">
                  <Controller
                    control={control}
                    name="firstName"
                    rules={{ required: t("firstNameError") }}
                    render={({ field, fieldState }) => (
                      <div className="w-full">
                        <InputGroup
                          label={t("firstName")}
                          placeholder={t("firstNamePlaceholder")}
                          error={!!fieldState.error}
                          errorMessage={fieldState.error?.message}
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          required
                        />
                      </div>
                    )}
                  />

                  <Controller
                    control={control}
                    name="lastName"
                    rules={{ required: t("lastNameError") }}
                    render={({ field, fieldState }) => (
                      <div className="w-full">
                        <InputGroup
                          label={t("lastName")}
                          placeholder={t("lastNamePlaceholder")}
                          error={!!fieldState.error}
                          errorMessage={fieldState.error?.message}
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          required
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-5 mb-5 lg:flex-row sm:gap-8">
                  <Controller
                    control={control}
                    name="subject"
                    rules={{ required: t("subjectError") }}
                    render={({ field, fieldState }) => (
                      <div className="w-full">
                        <InputGroup
                          label={t("subject")}
                          placeholder={t("subjectPlaceholder")}
                          error={!!fieldState.error}
                          errorMessage={fieldState.error?.message}
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          required
                        />
                      </div>
                    )}
                  />

                  <Controller
                    control={control}
                    name="phone"
                    rules={{
                      required: t("phoneError"),
                      validate: (value) => {
                        const digitsOnly = value.replace(/\D/g, "");
                        return (
                          digitsOnly.length >= 9 && digitsOnly.length <= 15
                        );
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <div className="w-full">
                        <InputGroup
                          type="tel"
                          label={t("phone")}
                          placeholder={t("phonePlaceholder")}
                          error={!!fieldState.error}
                          errorMessage={
                            fieldState.error?.type === "validate"
                              ? t("phoneValidationError")
                              : fieldState.error?.message
                          }
                          name={field.name}
                          value={field.value}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const val = e.target.value;
                            const filtered = val.replace(/[^0-9+()]/g, "");
                            field.onChange(filtered);
                          }}
                          maxLength={15}
                          required
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="mb-7.5">
                  <label htmlFor="message" className="block mb-2.5">
                    {t("message")}
                  </label>

                  <textarea
                    {...register("message", {
                      required: t("messageError"),
                    })}
                    id="message"
                    rows={5}
                    placeholder={t("messagePlaceholder")}
                    className="w-full p-5 duration-200 border rounded-2xl border-gray-3 bg-gray-1 placeholder:text-dark-5 outline-hidden focus:border-transparent focus:shadow-input focus:ring-2 focus:ring-blue/20"
                  />

                  {formState.errors.message && (
                    <p className="mt-1 text-sm text-red">
                      {formState.errors.message.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className={cn(
                    "inline-flex items-center gap-2 font-medium text-white bg-blue py-3 px-7 rounded-full ease-out duration-200 hover:bg-blue-dark",
                    {
                      "opacity-80 pointer-events-none": isLoading,
                    }
                  )}
                  disabled={isLoading}
                >
                  {t("sendMessage")} {isLoading && <Loader />}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
